import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileXlsIcon, FilePdfIcon, PlayIcon, MagnifyingGlassIcon, ArrowLeftIcon,
  FloppyDiskIcon, TrashIcon, ChartBarIcon, TableIcon, WarningIcon, CaretRightIcon, StarIcon,
} from "@phosphor-icons/react";
import MultiSelectCombo from "@/components/MultiSelectCombo";
import {
  buildQS, drillNext, DIM_LABEL, sortRows, searchRows, computeTotal, fmtMoney, fmtNum,
} from "./_dyn/format";
import ComparativeTable from "./_dyn/ComparativeTable";
import AnalisisPanel from "./_dyn/AnalisisPanel";
import KpiCards from "./_dyn/KpiCards";
import DrillTable from "./_dyn/DrillTable";
import { TrendChart, YearsChart, RankingChart } from "./_dyn/Charts";
import { getToken } from "@/lib/token";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const SS_KEY = "agroport.rcd.state.v2";

const DEFAULT_FILTERS = {
  desde: new Date().toISOString().slice(0, 8) + "01",
  hasta: new Date().toISOString().slice(0, 10),
  clientes: [], lineas: [], productos: [], categorias: [], rtcs: [], regiones: [], proyectos: [],
  presentaciones: [],
  metrica: "unidades",
  agrupar: "cliente",
  subagrupar: "",
  columnas: "",
  comparar_n: 1,
  condicion: "todos",
  comportamiento: "todos",
  crecimiento_filtro: "",
  crecimiento_min: "",
  crecimiento_base: "auto",
  separar_unidades: false,
  meses_inactividad: 0,
  top_n: 0,
  ordenar: "",
};

const loadSS = () => {
  try { return JSON.parse(sessionStorage.getItem(SS_KEY) || "null"); } catch { return null; }
};

const QUICK = [
  { k: "resumen", l: "Resumen", tab: "resumen", anchor: "kpi-cards" },
  { k: "tendencia", l: "Tendencia", tab: "resumen", anchor: "chart-tendencia" },
  { k: "top_clientes", l: "Top clientes", tab: "resumen", anchor: "chart-rank-clientes" },
  { k: "top_productos", l: "Top productos", tab: "resumen", anchor: "chart-rank-productos" },
  { k: "caidas", l: "Mayores caídas", tab: "analisis", anchor: "list-caidas", needComp: true },
  { k: "sin_compra", l: "Sin compra", tab: "analisis", anchor: "list-sin-compra", needComp: true },
];

export default function SalesDynamicReport() {
  const ss = loadSS();
  // Estado único del reporte: filtros + vista + orden + búsqueda + columnas + pila de drill
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...(ss?.filters || {}) });
  const [tab, setTab] = useState(ss?.tab || "resumen"); // resumen | analisis | detalle
  const [sort, setSort] = useState(ss?.sort || { key: "", dir: "desc" });
  const [search, setSearch] = useState(ss?.search || "");
  const [hidden, setHidden] = useState(ss?.hidden || []);
  const [options, setOptions] = useState(null);
  const [data, setData] = useState(null);
  const [runParams, setRunParams] = useState(null); // parámetros efectivos del reporte visible
  const [loading, setLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [drillStack, setDrillStack] = useState([]); // [{dim, id, label, snap}]
  const [drill, setDrill] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);

  const setF = (patch) => setFilters((p) => ({ ...p, ...patch }));

  useEffect(() => {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify({ filters, tab, sort, search, hidden })); } catch { /* sin storage */ }
  }, [filters, tab, sort, search, hidden]);

  useEffect(() => {
    (async () => {
      try {
        const [o, v] = await Promise.all([
          api.get("/reports2/ventas-dinamico/opciones"),
          api.get("/reports2/ventas-dinamico/views"),
        ]);
        setOptions(o.data);
        setSavedViews(v.data || []);
      } catch {
        toast.error("No se pudieron cargar los filtros");
      }
    })();
  }, []);

  // Productos dependientes de las líneas/categorías seleccionadas
  useEffect(() => {
    if (!options) return;
    const p = new URLSearchParams();
    if (filters.lineas.length) p.set("lineas", filters.lineas.join(","));
    if (filters.categorias.length) p.set("categorias", filters.categorias.join(","));
    api.get(`/reports2/ventas-dinamico/opciones?${p.toString()}`)
      .then((r) => setOptions((prev) => ({ ...prev, productos: r.data.productos })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.lineas, filters.categorias]);

  const fetchRun = async (params) => {
    const { data: d } = await api.get(`/reports2/ventas-dinamico/run?${buildQS(params)}`);
    setData(d);
    setRunParams(params);
    return d;
  };

  const run = async (overrides = null) => {
    const isValid = overrides && typeof overrides === "object" && !overrides.nativeEvent
      && ("desde" in overrides || "agrupar" in overrides);
    const effF = isValid ? { ...DEFAULT_FILTERS, ...overrides } : filters;
    setLoading(true);
    try {
      await fetchRun({ ...effF, fijar: [] });
      setDrillStack([]); setDrill(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando reporte");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  // El reporte se actualiza solo al cambiar cualquier filtro (no hace falta pulsar "Generar reporte")
  const primeraVez = useRef(true);
  useEffect(() => {
    if (primeraVez.current) { primeraVez.current = false; return; }
    const t = setTimeout(() => run(), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // Parámetros de la vista visible (búsqueda + orden + columnas) → export = pantalla
  const viewQS = () => {
    const p = { ...(runParams || filters), buscar: search.trim(), sort_key: sort.key, sort_dir: sort.dir,
      ocultar_cols: hidden };
    return buildQS(p);
  };

  const download = async (fmt) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/reports2/ventas-dinamico/run?${viewQS()}&format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `reporte-comercial-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      toast.success(fmt === "pdf" ? "PDF descargado" : "Excel descargado");
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const saveView = async () => {
    if (!saveName.trim()) return;
    try {
      await api.post("/reports2/ventas-dinamico/views", {
        nombre: saveName.trim(), filtros: filters, vista: tab, orden: sort, columnas_ocultas: hidden,
      });
      const { data: v } = await api.get("/reports2/ventas-dinamico/views");
      setSavedViews(v || []);
      setSaveName(""); setSaveOpen(false);
      toast.success("Vista guardada (configuración; los datos se recalculan al abrir)");
    } catch { toast.error("Error al guardar"); }
  };
  const loadView = (v) => {
    const merged = { ...DEFAULT_FILTERS, ...v.filtros };
    setFilters(merged);
    if (v.vista) setTab(v.vista);
    setSort(v.orden?.key !== undefined ? v.orden : { key: "", dir: "desc" });
    setHidden(v.columnas_ocultas || []);
    run(merged);
  };
  const deleteView = async (vid) => {
    if (!window.confirm("¿Eliminar la vista guardada?")) return;
    try {
      await api.delete(`/reports2/ventas-dinamico/views/${vid}`);
      setSavedViews((s) => s.filter((v) => v.id !== vid));
      toast.success("Vista eliminada");
    } catch { toast.error("Error"); }
  };

  // ── Drill-down con pila de navegación (cada nivel guarda el contexto completo para REGRESAR) ──
  const drillAnchor = drillStack[0]?.dim || data?.meta?.dim || filters.agrupar;
  const nextDim = data ? drillNext(drillAnchor, drillStack.length) : null;

  const openDrill = async (row) => {
    if (!data || !runParams || !nextDim) return;
    const key = row.grupo_key ?? row.grupo_id;
    const fijar = [...(runParams.fijar || []), [data.meta.dim, String(key)]];
    if (row.unidad && data.meta.separar_unidades) fijar.push(["presentacion", row.unidad]);
    const snap = { data, runParams, tab, search, sort, drill };
    const entry = { dim: data.meta.dim, id: key, label: row.grupo, snap };
    setLoading(true);
    try {
      if (nextDim === "factura") {
        const p = new URLSearchParams(buildQS({ ...runParams, fijar }));
        p.set("limit", "1000");
        const { data: d } = await api.get(`/reports2/ventas-dinamico/drill?${p.toString()}`);
        setDrill(d);
      } else {
        await fetchRun({ ...runParams, fijar, agrupar: nextDim, subagrupar: "", top_n: 0 });
        setDrill(null);
      }
      setDrillStack((s) => [...s, entry]);
      setSearch("");
      setSort({ key: "", dir: "desc" });
      setTab("detalle");
    } catch {
      toast.error("Error al profundizar");
    } finally { setLoading(false); }
  };

  const goBack = () => {
    if (!drillStack.length) return;
    const last = drillStack[drillStack.length - 1];
    const s = last.snap;
    setData(s.data); setRunParams(s.runParams); setTab(s.tab); setSearch(s.search);
    setSort(s.sort); setDrill(s.drill);
    setDrillStack(drillStack.slice(0, -1));
  };

  const resetAll = async () => {
    setDrillStack([]); setDrill(null); setSearch(""); setSort({ key: "", dir: "desc" });
    await run();
  };

  const quick = async (q) => {
    setTab(q.tab);
    if (q.needComp && (data?.meta?.periodos || []).length < 2) {
      const f2 = { ...filters, comparar_n: 1 };
      setFilters(f2);
      await run(f2);
    }
    setTimeout(() => {
      const el = document.getElementById(q.anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const onSort = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "desc" ? "asc" : "desc" } : { key: k, dir: "desc" }));
  const toggleCol = (k) => setHidden((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));

  const periodos = useMemo(() => data?.meta?.periodos || [], [data]);
  const visibleRows = useMemo(() => {
    if (!data) return [];
    return sortRows(searchRows(data.rows, search), sort.key, sort.dir);
  }, [data, search, sort]);
  const visibleTotal = useMemo(() => {
    if (!data) return null;
    return search.trim() ? computeTotal(visibleRows, periodos) : data.total;
  }, [data, search, visibleRows, periodos]);
  const dimLabel = DIM_LABEL[data?.meta?.dim] || "Grupo";
  const metrica = data?.meta?.metrica || filters.metrica;

  // ─────── Render ───────
  return (
    <div className="space-y-4" data-testid="sales-dynamic-report">
      {/* Cabecera + acciones */}
      <div className="bg-card border border-border/60 rounded-lg p-4">
        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-semibold">Reporte Comercial Dinámico</h2>
            <p className="text-xs text-text-muted mt-0.5">
              {periodos.length > 1
                ? `${periodos.length} períodos · comparación año contra año`
                : "Selecciona el período y filtros para analizar tu operación comercial"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {savedViews.length > 0 && (
              <select
                onChange={(e) => {
                  const v = savedViews.find((sv) => sv.id === e.target.value);
                  if (v) loadView(v);
                }}
                className="bg-app border border-border rounded-md px-2 py-2 text-xs"
                data-testid="saved-views-select"
                defaultValue=""
              >
                <option value="" disabled>Vistas guardadas</option>
                {savedViews.map((v) => <option key={v.id} value={v.id}>{v.nombre}</option>)}
              </select>
            )}
            <button onClick={() => setSaveOpen(true)} data-testid="btn-save-view"
              className="border border-border rounded-md px-3 py-2 text-xs flex items-center gap-1.5 hover:bg-white/[0.04]">
              <FloppyDiskIcon size={12} /> Guardar vista
            </button>
            <button onClick={() => download("xlsx")} disabled={!data} data-testid="btn-export-xlsx"
              className="border border-agro-green/50 text-agro-green hover:bg-agro-green/10 rounded-md px-3 py-2 text-xs flex items-center gap-1.5 disabled:opacity-40">
              <FileXlsIcon size={13} weight="bold" /> Excel
            </button>
            <button onClick={() => download("pdf")} disabled={!data} data-testid="btn-export-pdf"
              className="border border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-md px-3 py-2 text-xs flex items-center gap-1.5 disabled:opacity-40">
              <FilePdfIcon size={13} weight="bold" /> PDF
            </button>
          </div>
        </div>

        {/* Bloque de filtros principales */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <L label="Desde">
            <input type="date" value={filters.desde} onChange={(e) => setF({ desde: e.target.value })}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-desde" />
          </L>
          <L label="Hasta">
            <input type="date" value={filters.hasta} onChange={(e) => setF({ hasta: e.target.value })}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-hasta" />
          </L>
          <L label="Comparar vs">
            <select value={filters.comparar_n} onChange={(e) => setF({ comparar_n: Number(e.target.value) })}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-comparar">
              <option value={0}>Sin comparación</option>
              <option value={1}>1 período anterior</option>
              <option value={2}>2 períodos anteriores</option>
              <option value={3}>3 períodos anteriores</option>
              <option value={4}>4 períodos anteriores</option>
              <option value={5}>5 períodos anteriores</option>
            </select>
          </L>
          <L label="Mostrar resultados en">
            <select value={filters.metrica} onChange={(e) => setF({ metrica: e.target.value })}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-metrica">
              <option value="unidades">Unidades vendidas</option>
              <option value="monto">Monto vendido</option>
              <option value="ambos">Unidades + Monto</option>
            </select>
          </L>
          <L label="Agrupar por">
            <select value={filters.agrupar}
              onChange={(e) => {
                const nuevo = e.target.value;
                // §Informe v4 · Al agrupar por PRODUCTO, sugerir automáticamente subagrupar=cliente
                setF({
                  agrupar: nuevo,
                  ...(nuevo === "producto" && !filters.subagrupar ? { subagrupar: "cliente" } : {}),
                });
              }}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-agrupar">
              <option value="cliente">Cliente</option>
              <option value="producto">Producto</option>
              <option value="linea">Línea</option>
              <option value="categoria">Categoría</option>
              <option value="rtc">RTC</option>
              <option value="region">Región</option>
              <option value="proyecto">Proyecto</option>
              <option value="presentacion">Presentación</option>
              <option value="proveedor">Proveedor (línea real)</option>
              <option value="subcategoria">Subcategoría</option>
              <option value="nivel_precio">Nivel de precio</option>
              <option value="canal">Canal</option>
              <option value="semana">Semana</option>
              <option value="mes">Mes</option>
              <option value="anio">Año</option>
            </select>
          </L>
          <L label="Subagrupar por (opcional)">
            <select value={filters.subagrupar} onChange={(e) => setF({ subagrupar: e.target.value })}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-subagrupar">
              <option value="">— Ninguno —</option>
              <option value="cliente">Cliente</option>
              <option value="producto">Producto</option>
              <option value="linea">Línea</option>
              <option value="categoria">Categoría</option>
              <option value="rtc">RTC</option>
              <option value="region">Región</option>
              <option value="proyecto">Proyecto</option>
              <option value="proveedor">Proveedor (línea real)</option>
              <option value="subcategoria">Subcategoría</option>
              <option value="nivel_precio">Nivel de precio</option>
              <option value="canal">Canal</option>
              <option value="mes">Mes</option>
            </select>
          </L>
          <L label="DIVIDIR EN COLUMNAS POR (TOTAL AL FINAL)">
            <select value={filters.columnas} onChange={(e) => setF({ columnas: e.target.value })}
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-columnas">
              <option value="">— Solo períodos —</option>
              <option value="mes">Mes</option>
              <option value="semana">Semana</option>
              <option value="anio">Año</option>
              <option value="categoria">Categoría</option>
              <option value="proveedor">Proveedor</option>
              <option value="producto">Producto</option>
              <option value="cliente">Cliente</option>
              <option value="region">Región</option>
              <option value="rtc">RTC</option>
              <option value="nivel_precio">Nivel de precio</option>
              <option value="canal">Canal</option>
            </select>
          </L>
          <MultiSelectCombo label="Clientes" testid="filter-clientes" placeholder="Todos"
            items={(options?.clientes || []).map((c) => ({ id: c.id, label: c.nombre, codigo: c.codigo, rtn: c.rtn, sub: c.codigo || c.rtn || "" }))}
            value={filters.clientes} onChange={(v) => setF({ clientes: v })}
            searchKeys={["codigo", "rtn"]} />
          <MultiSelectCombo label="Líneas" testid="filter-lineas" placeholder="Todas"
            items={options?.lineas || []} value={filters.lineas} onChange={(v) => setF({ lineas: v, productos: [] })} />
          <MultiSelectCombo label="Productos" testid="filter-productos" placeholder="Todos"
            items={(options?.productos || []).map((p) => ({ id: p.id, label: p.nombre, codigo: p.codigo, categoria: p.categoria, sub: p.historico ? "VENTA HISTÓRICA · no existe en el catálogo de productos" : `${p.linea} › ${p.categoria} › ${p.unidad}` }))}
            value={filters.productos} onChange={(v) => setF({ productos: v })}
            searchKeys={["codigo", "categoria"]} />
          <MultiSelectCombo label="Regiones" testid="filter-regiones" placeholder="Todas"
            items={options?.regiones || []} value={filters.regiones} onChange={(v) => setF({ regiones: v })} />
          <label className="flex flex-col gap-1">
            <span className="text-[9px] uppercase tracking-wider text-text-muted">DIVISIÓN POR REGIÓN</span>
            <button type="button" onClick={() => setF({ columnas: filters.columnas === "region" ? "" : "region" })}
              data-testid="toggle-dividir-region"
              className={`rounded-md px-2.5 py-2 text-xs font-semibold border text-left ${filters.columnas === "region"
                ? "border-agro-green bg-agro-green/10 text-agro-green" : "border-border text-text-secondary"}`}>
              {filters.columnas === "region" ? "DIVIDIDO POR REGIÓN (R1…R5 + TOTAL)" : "DIVIDIR POR REGIÓN"}
            </button>
          </label>
          <MultiSelectCombo label="RTC" testid="filter-rtcs" placeholder="Todos"
            items={(options?.rtcs || []).map((r) => ({ id: r.id, label: r.nombre }))}
            value={filters.rtcs} onChange={(v) => setF({ rtcs: v })} />
          <MultiSelectCombo label="Proyectos" testid="filter-proyectos" placeholder="Todos"
            items={options?.proyectos || []} value={filters.proyectos} onChange={(v) => setF({ proyectos: v })} />

          {showMore && (
            <>
              <MultiSelectCombo label="Categorías" testid="filter-categorias" placeholder="Todas"
                items={options?.categorias || []} value={filters.categorias} onChange={(v) => setF({ categorias: v, productos: [] })} />
              <MultiSelectCombo label="Presentación / Unidad" testid="filter-presentaciones" placeholder="Todas"
                items={options?.presentaciones || []} value={filters.presentaciones} onChange={(v) => setF({ presentaciones: v })} />
              <L label="Condición de venta">
                <select value={filters.condicion} onChange={(e) => setF({ condicion: e.target.value })}
                  className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-condicion">
                  <option value="todos">Todos</option>
                  <option value="contado">Contado</option>
                  <option value="credito">Crédito</option>
                </select>
              </L>
              <L label="Comportamiento cliente">
                <select value={filters.comportamiento} onChange={(e) => setF({ comportamiento: e.target.value })}
                  className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-comportamiento">
                  <option value="todos">Todos</option>
                  <option value="crecimiento">Crecimiento</option>
                  <option value="caida">Caída</option>
                  <option value="sin_compra">Sin compra</option>
                  <option value="nuevo">Nuevo</option>
                  <option value="reactivado">Reactivado</option>
                </select>
              </L>
              <L label="Filtro variación">
                <select value={filters.crecimiento_filtro} onChange={(e) => setF({ crecimiento_filtro: e.target.value })}
                  className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-crecimiento">
                  <option value="">Todos</option>
                  <option value="crecimiento">Crecimiento &gt;0%</option>
                  <option value="crec_10">Crecimiento &gt;10%</option>
                  <option value="crec_20">Crecimiento &gt;20%</option>
                  <option value="caida">Caída &lt;0%</option>
                  <option value="caida_10">Caída &lt;-10%</option>
                  <option value="caida_20">Caída &lt;-20%</option>
                  <option value="sin_compra">Sin compra</option>
                  <option value="custom">Personalizado…</option>
                </select>
              </L>
              {filters.crecimiento_filtro === "custom" && (
                <L label="Umbral personalizado (%)">
                  <input type="number" step="0.1" value={filters.crecimiento_min}
                    onChange={(e) => setF({ crecimiento_min: e.target.value })}
                    placeholder="Ej: -30 (caída ≥ 30%) o 25 (crec ≥ 25%)"
                    className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full"
                    data-testid="filter-crecimiento-min" />
                </L>
              )}
              <L label="Reactivado: meses sin compra">
                <select value={filters.meses_inactividad} onChange={(e) => setF({ meses_inactividad: Number(e.target.value) })}
                  className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-meses-inactividad">
                  <option value={0}>Período comparable anterior</option>
                  <option value={3}>3 meses</option>
                  <option value={6}>6 meses</option>
                  <option value={12}>12 meses</option>
                  <option value={24}>24 meses</option>
                </select>
              </L>
              <L label="Unidades de medida">
                <label className="flex items-center gap-2 text-xs py-2">
                  <input type="checkbox" checked={!!filters.separar_unidades}
                    onChange={(e) => setF({ separar_unidades: e.target.checked })} data-testid="filter-separar-unidades" />
                  Separar filas por unidad
                </label>
              </L>
              <L label="Ranking (Top/Bottom-N según orden)">
                <select value={filters.top_n} onChange={(e) => setF({ top_n: Number(e.target.value) })}
                  className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-topn">
                  <option value={0}>Todos</option>
                  <option value={5}>Top 5</option>
                  <option value={10}>Top 10</option>
                  <option value={20}>Top 20</option>
                  <option value={50}>Top 50</option>
                </select>
              </L>
              <L label="Ordenar por">
                <select value={filters.ordenar} onChange={(e) => setF({ ordenar: e.target.value })}
                  className="bg-app border border-border rounded-md px-2.5 py-2 text-xs w-full" data-testid="filter-ordenar">
                  <option value="">Métrica principal</option>
                  <option value="unidades_desc">Unidades ↓</option>
                  <option value="monto_desc">Monto ↓</option>
                  <option value="variacion_desc">Mayor crecimiento</option>
                  <option value="caida_desc">Mayor caída</option>
                  <option value="facturas_desc">Más facturas</option>
                  <option value="monto_asc">Monto ↑ (menores)</option>
                  <option value="unidades_asc">Unidades ↑ (menores)</option>
                  <option value="variacion_und_desc">Mayor crecimiento (unidades)</option>
                  <option value="caida_und_desc">Mayor caída (unidades)</option>
                  <option value="nombre">Nombre A-Z</option>
                </select>
              </L>
            </>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
          <button onClick={() => setShowMore((v) => !v)} className="text-xs text-agro-green hover:underline"
            data-testid="toggle-more-filters">
            {showMore ? "− Menos filtros" : "+ Más filtros"}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={resetAll} className="text-xs text-text-muted hover:text-white"
              data-testid="btn-reset">
              Reiniciar
            </button>
            <button onClick={() => run()} disabled={loading} data-testid="btn-run"
              className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-5 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50">
              <PlayIcon size={13} weight="bold" /> {loading ? "Generando…" : "Generar reporte"}
            </button>
          </div>
        </div>

        {saveOpen && (
          <div className="mt-3 border-t border-border/60 pt-3 flex items-center gap-2">
            <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Nombre de la vista"
              className="bg-app border border-border rounded-md px-2.5 py-2 text-xs flex-1 max-w-md"
              data-testid="input-save-name" />
            <button onClick={saveView} className="bg-agro-green text-black rounded-md px-3 py-2 text-xs font-medium"
              data-testid="btn-confirm-save">Guardar</button>
            <button onClick={() => setSaveOpen(false)} className="text-xs text-text-muted">Cancelar</button>
            {savedViews.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[10px] text-text-muted">Guardadas:</span>
                {savedViews.map((v) => (
                  <span key={v.id} className="bg-white/5 text-xs rounded px-2 py-0.5 flex items-center gap-1">
                    {v.nombre}
                    <button aria-label="Eliminar" onClick={() => deleteView(v.id)} className="text-text-muted hover:text-red-400"
                      data-testid={`btn-delete-view-${v.id}`}>
                      <TrashIcon size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Accesos rápidos */}
      {data && (
        <div className="flex flex-wrap items-center gap-2" data-testid="quick-access">
          {QUICK.map((q) => (
            <button key={q.k} onClick={() => quick(q)} data-testid={`quick-${q.k}`}
              className="text-[10px] uppercase tracking-wider border border-border rounded-full px-3 py-1 hover:border-agro-green hover:text-agro-green">
              {q.l}
            </button>
          ))}
        </div>
      )}

      {/* Pila de drill-down: ← REGRESAR restaura el nivel anterior sin perder filtros */}
      {drillStack.length > 0 && (
        <div className="flex items-center gap-2 text-xs bg-agro-green/5 border border-agro-green/30 rounded-md px-3 py-2 flex-wrap"
          data-testid="drill-breadcrumb">
          <button onClick={goBack} className="text-agro-green hover:underline flex items-center gap-1 font-semibold"
            data-testid="btn-drill-back">
            <ArrowLeftIcon size={12} /> REGRESAR
          </button>
          <span className="text-text-muted">›</span>
          {drillStack.map((s, i) => (
            <Fragment key={i}>
              <span className="text-text-muted">{DIM_LABEL[s.dim]}:</span>
              <span className="text-white">{s.label}</span>
              <CaretRightIcon size={10} className="text-text-muted" />
            </Fragment>
          ))}
          <span className="text-agro-green">{drill ? "Facturas" : dimLabel}</span>
        </div>
      )}

      {data?.meta?.aviso_unidades_mixtas && metrica !== "monto" && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 text-xs text-amber-300 flex items-center gap-2 flex-wrap"
          data-testid="warning-mixed-units">
          <WarningIcon size={14} />
          La consulta mezcla unidades de medida distintas: no se suman entre sí; se muestran separadas por unidad (celdas en ámbar y totales por unidad).
          {!filters.separar_unidades && (
            <button className="underline" onClick={() => { const f2 = { ...filters, separar_unidades: true }; setFilters(f2); run(f2); }}
              data-testid="btn-separar-unidades">Separar filas por unidad</button>
          )}
        </div>
      )}

      {data && !drill && <KpiCards kpis={data.kpis} periodos={periodos} />}

      {data && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="border-b border-border/60 flex items-center flex-wrap">
            <TabBtn active={tab === "resumen"} onClick={() => setTab("resumen")} icon={<ChartBarIcon size={12} />} testid="tab-resumen">Resumen</TabBtn>
            <TabBtn active={tab === "analisis"} onClick={() => setTab("analisis")} icon={<StarIcon size={12} />} testid="tab-analisis">Análisis</TabBtn>
            <TabBtn active={tab === "detalle"} onClick={() => setTab("detalle")} icon={<TableIcon size={12} />} testid="tab-detalle">Detalle</TabBtn>
            <div className="ml-auto p-3 relative">
              <MagnifyingGlassIcon size={12} className="absolute left-5 top-1/2 -translate-y-1/2 text-text-muted" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar en resultados…"
                className="bg-app border border-border rounded-md pl-7 pr-2 py-1.5 text-xs w-52"
                data-testid="search-results" />
            </div>
          </div>

          {tab === "resumen" && (
            <div className="p-4 space-y-4" data-testid="view-resumen">
              {data.pivot && <PivotView data={data} rows={visibleRows} />}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TrendChart trend={data.trend} periodos={periodos} metrica={metrica} />
                <YearsChart total={visibleTotal} periodos={periodos} metrica={metrica} />
                <RankingChart title="Ranking de clientes (Top 10)" id="chart-rank-clientes"
                  items={data.rankings?.clientes} metrica={metrica} periodos={periodos} />
                <RankingChart title="Ranking de productos (Top 10)" id="chart-rank-productos"
                  items={data.rankings?.productos} metrica={metrica} periodos={periodos} />
              </div>
              {periodos.length > 1 && <BehaviorCounts counts={data.kpis.comportamiento_counts || {}} />}
            </div>
          )}
          {tab === "analisis" && (
            <AnalisisPanel rows={visibleRows} hasComp={periodos.length > 1} onOpen={openDrill} dimLabel={dimLabel} />
          )}
          {tab === "detalle" && (
            drill ? <DrillTable drill={drill} onBack={goBack} />
              : (
                <>
                  {data.pivot && <PivotView data={data} rows={visibleRows} />}
                  <ComparativeTable columns={data.columns} rows={visibleRows} total={visibleTotal} periodos={periodos}
                    metrica={metrica} dimLabel={dimLabel} sort={sort} onSort={onSort} hidden={hidden}
                    onToggleCol={toggleCol} onOpen={openDrill} canDrill={!!nextDim} />
                </>
              )
          )}
        </div>
      )}
    </div>
  );
}

// ─────────── SUBCOMPONENTS ───────────

function BehaviorCounts({ counts }) {
  const items = [
    { k: "crecimiento", label: "En crecimiento", color: "text-emerald-400" },
    { k: "caida", label: "En caída", color: "text-red-400" },
    { k: "sin_compra", label: "Sin compra (riesgo)", color: "text-amber-400" },
    { k: "nuevo", label: "Nuevos", color: "text-sky-400" },
    { k: "reactivado", label: "Reactivados", color: "text-violet-400" },
    { k: "estable", label: "Estables", color: "text-text-muted" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="behavior-cards">
      {items.map((c) => (
        <div key={c.k} className="bg-app/40 border border-border/40 rounded p-2">
          <div className="text-[9px] uppercase text-text-muted tracking-wider">{c.label}</div>
          <div className={`text-lg font-semibold ${c.color}`} data-testid={`count-${c.k}`}>{counts[c.k] || 0}</div>
        </div>
      ))}
    </div>
  );
}

function PivotView({ data, rows }) {
  const cols = data.pivot?.columnas || [];
  const metric = data.meta?.metrica === "monto" ? "monto" : "unidades";
  const fmt = (v) => (metric === "monto" ? fmtMoney(v, data.meta?.moneda) : fmtNum(v));
  const total = (r) => cols.reduce((acc, c) => acc + ((r.cols || {})[c.key]?.[metric] || 0), 0);
  return (
    <div className="p-4 border-b border-border/60 overflow-x-auto" data-testid="pivot-view">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
        Tabla dinámica · filas: {data.meta?.dim} × columnas: {data.pivot?.dim_columnas} ({metric})
      </div>
      <table className="w-full text-xs">
        <thead className="bg-sidebar/60">
          <tr>
            <th className="p-2 text-left sticky left-0 bg-sidebar">Grupo</th>
            {cols.map((c) => <th key={c.key} className="p-2 text-right whitespace-nowrap">{c.label}</th>)}
            <th className="p-2 text-right text-agro-green">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border/30">
              <td className="p-2 sticky left-0 bg-card">{r.grupo}</td>
              {cols.map((c) => {
                const v = (r.cols || {})[c.key]?.[metric] || 0;
                return <td key={c.key} className={`p-2 text-right ${v ? "" : "text-text-muted"}`}>{v ? fmt(v) : "—"}</td>;
              })}
              <td className="p-2 text-right font-medium text-agro-green">{fmt(total(r))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-border">
          <tr>
            <td className="p-2 font-semibold sticky left-0 bg-card">TOTAL GENERAL</td>
            {cols.map((c) => <td key={c.key} className="p-2 text-right font-medium">{fmt(c[metric])}</td>)}
            <td className="p-2 text-right font-semibold text-agro-green">{fmt(cols.reduce((a, c) => a + c[metric], 0))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[9px] uppercase tracking-wider text-text-muted">{label}</span>
      {children}
    </label>
  );
}

function TabBtn({ active, onClick, children, icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-3 text-xs flex items-center gap-1.5 border-b-2 ${
        active ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-white"
      }`}>
      {icon} {children}
    </button>
  );
}
